import { HttpErrorResponse } from '@angular/common/http';
import { Inject, Injectable, signal, WritableSignal } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { ApiService } from './api.service';
import { ApiUrl } from './constants';
import {
  IHistoricalDetailResponse,
  RequestParams,
} from '@src/app/shared/types/common';
import { RoutesEnum } from '@src/app/enums';
import { Router } from '@angular/router';

@Injectable({
  providedIn: 'root',
})
export class InvestService {
  private isInvestMode: WritableSignal<boolean | null> = signal(null);

  private isLoadingSubject = new BehaviorSubject<boolean>(false);
  isLoading$: Observable<boolean> = this.isLoadingSubject.asObservable();

  private isHistoricalDetailsLoadingSubject = new BehaviorSubject<boolean>(
    false,
  );
  isHistoricalDetailsLoading$: Observable<boolean> =
    this.isHistoricalDetailsLoadingSubject.asObservable();

  constructor(
    @Inject(Router) private router: Router,
    private apiService: ApiService,
  ) {}

  get isLoading(): boolean {
    return this.isLoadingSubject.value;
  }

  get isHistoricalDetailsLoading(): boolean {
    return this.isHistoricalDetailsLoadingSubject.value;
  }

  getIsInvestMode(): boolean | null {
    return this.isInvestMode();
  }

  setIsInvestMode(value: boolean | null) {
    this.isInvestMode.set(value);
  }

  setIsLoading(value: boolean): void {
    this.isLoadingSubject.next(value);
  }

  setIsHistoricalDetailsLoading(value: boolean): void {
    this.isHistoricalDetailsLoadingSubject.next(value);
  }

  handleError(error: HttpErrorResponse): void {
    this.isLoadingSubject.next(false);
    this.setIsHistoricalDetailsLoading(false);

    if (
      error.error?.statusCode === 404 ||
      error.error?.status === 404 ||
      error?.status === 404
    ) {
      this.router.navigate(['/' + RoutesEnum.NOT_FOUND], {
        skipLocationChange: true,
      });
    } else if (error.error?.message) {
      console.error(error.error?.message);
    }
  }

  getInvestContentPage(
    params: RequestParams,
  ): Observable<Record<string, any>[]> {
    this.isLoadingSubject.next(true);

    return this.apiService.request({
      method: 'get',
      endpoint: ApiUrl.getInvestContent,
      params,
    });
  }

  getInvestManagementPage(
    params: RequestParams,
  ): Observable<Record<string, any>> {
    this.isLoadingSubject.next(true);

    return this.apiService.request({
      method: 'get',
      endpoint: ApiUrl.getInvestManagement,
      params,
    });
  }

  getInvestInvestorPage(
    params: RequestParams,
  ): Observable<Record<string, any>> {
    this.isLoadingSubject.next(true);

    return this.apiService.request({
      method: 'get',
      endpoint: ApiUrl.getInvestInvestor,
      params,
    });
  }

  getInvestResultsPage(params: RequestParams): Observable<Record<string, any>> {
    this.isLoadingSubject.next(true);

    return this.apiService.request({
      method: 'get',
      endpoint: ApiUrl.getInvestResults,
      params,
    });
  }

  postInvestContact(
    body: Record<string, any>,
    params?: any,
  ): Observable<Record<string, any>> {
    return this.apiService.request({
      method: 'post',
      endpoint: ApiUrl.postInvestContact,
      params,
      body,
    });
  }

  getHistoricalDetails(
    body: Record<string, any>,
    params?: any,
  ): Observable<IHistoricalDetailResponse> {
    this.setIsHistoricalDetailsLoading(true);

    return this.apiService.request({
      method: 'post',
      endpoint: ApiUrl.getHistoricalDetails,
      params,
      body,
    });
  }
}
